import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import API_BASE from '../services/apiBase';
import AuthSplitShell from '../components/AuthSplitShell';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token.trim()) {
      setError('This page is missing a valid reset link. Open the link from your email or request a new reset.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: token.trim(), newPassword: password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || 'Could not reset password.');
        return;
      }
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true, state: { message: data.message } }), 2000);
    } catch {
      setError('Network error. Try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token && !done) {
    return (
      <AuthSplitShell title="Invalid reset link" subtitle="Use the link from your email, or request a new password reset.">
        <div className="auth-split-card" style={{ textAlign: 'center' }}>
          <AlertCircle className="w-10 h-10 text-amber-600" style={{ margin: '0 auto 1rem' }} />
          <Link to="/forgot-password" className="btn" style={{ marginTop: '0.5rem', display: 'inline-flex' }}>
            Request reset
          </Link>
          <p className="auth-split-links" style={{ marginTop: '1.25rem', marginBottom: 0 }}>
            <Link to="/login">Back to sign in</Link>
          </p>
        </div>
      </AuthSplitShell>
    );
  }

  return (
    <AuthSplitShell title="Set new password" subtitle="Choose a strong password for your account.">
      <motion.div
        className="auth-split-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        {done ? (
          <div className="auth-split-msg-success" style={{ marginBottom: 0, textAlign: 'left' }}>
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <p style={{ margin: 0, fontWeight: 600 }}>Password updated. Redirecting to sign in…</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>New password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`form-input ${error ? 'error' : ''}`}
                    autoComplete="new-password"
                    placeholder="At least 6 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Confirm password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="form-input"
                  autoComplete="new-password"
                />
              </div>

              {error && (
                <div className="auth-split-msg-error" style={{ marginBottom: '1.25rem', color: '#b91c1c' }}>
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn"
                style={{
                  width: '100%',
                  opacity: isSubmitting ? 0.7 : 1,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                }}
              >
                {isSubmitting ? 'Saving…' : 'Update password'}
              </button>
          </form>
        )}
      </motion.div>

      <p className="auth-split-links" style={{ textAlign: 'center', marginTop: '1.5rem', marginBottom: 0 }}>
        <Link to="/login">Back to sign in</Link>
      </p>
    </AuthSplitShell>
  );
};

export default ResetPassword;
